import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const WelcomeScreen = ({ onTopicSelect, onCardClick, onStartDemo, isDemoReady }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredCard, setHoveredCard] = useState(null);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const scenarios = [
    {
      id: 'excel',
      title: '직장인 엑셀 자동화',
      desc: '파이썬 기초로 엑셀 업무 자동화 시작하기',
      icon: 'fa-file-excel',
      color: 'text-sky-400',
      bgGradient: 'from-sky-500/10 to-blue-500/5',
      border: 'group-hover:border-sky-500/30',
      shadow: 'group-hover:shadow-[0_0_20px_rgba(14,165,233,0.1)]',
      prompt: '매일 반복되는 엑셀 업무를 파이썬으로 자동화하고 싶어. 어떻게 시작하면 좋을까?',
      badge: 'Short-term',
      badgeColor: 'bg-sky-500/20 text-sky-300 border-sky-500/30'
    },
    {
      id: 'web',
      title: '나만의 웹 포트폴리오',
      desc: 'React로 나만의 차별화된 포트폴리오 사이트 완성',
      icon: 'fa-laptop-code',
      color: 'text-emerald-400',
      bgGradient: 'from-emerald-500/10 to-teal-500/5',
      border: 'group-hover:border-emerald-500/30',
      shadow: 'group-hover:shadow-[0_0_20px_rgba(16,185,129,0.1)]',
      prompt: 'React로 나만의 포트폴리오 사이트를 완성해보고 싶어. 커리큘럼을 추천해줘.',
      badge: 'Master Course',
      badgeColor: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30'
    },
    {
      id: 'stock',
      title: '주식 분석 대시보드',
      desc: '실시간 주식 데이터 분석 및 대시보드 시각화 구현',
      icon: 'fa-chart-line',
      color: 'text-emerald-400',
      bgGradient: 'from-emerald-500/10 to-teal-500/5',
      border: 'group-hover:border-emerald-500/30',
      shadow: 'group-hover:shadow-[0_0_20px_rgba(16,185,129,0.1)]',
      prompt: '관심 있는 주식 데이터를 분석해서 시각화하는 대시보드를 만들고 싶어.',
      badge: 'Master Course',
      badgeColor: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30'
    },
    {
      id: 'career',
      title: '풀스택 개발자 커리어',
      desc: '기초부터 취업까지, 6개월 풀스택 완성 로드맵',
      icon: 'fa-rocket',
      color: 'text-purple-400',
      bgGradient: 'from-purple-500/10 to-violet-500/5',
      border: 'group-hover:border-purple-500/30',
      shadow: 'group-hover:shadow-[0_0_20px_rgba(168,85,247,0.1)]',
      prompt: '풀스택 개발자로 취업하기 위한 체계적인 학습 로드맵이 궁금해.',
      badge: 'Career Map',
      badgeColor: 'bg-purple-500/20 text-purple-300 border-purple-500/30'
    }
  ];

  return (
    <div className="h-full flex flex-col items-center justify-center relative overflow-hidden p-8">
      {/* Background Decor */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl animate-pulse-slow"></div>
        <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-purple-500/5 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '2s' }}></div>
      </div>

      <AnimatePresence mode="wait">
        {isDemoReady ? (
          <motion.div
            key="ready"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.05 }}
            transition={{ type: "spring", damping: 20, stiffness: 300 }}
            className="w-full max-w-md z-10 relative"
          >
            {/* Compact Card Design */}
            <div className="relative bg-slate-900/60 backdrop-blur-xl border border-white/10 rounded-3xl p-8 overflow-hidden shadow-2xl">
              {/* Background Glow */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-purple-500/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>

              {/* Content */}
              <div className="relative z-10 flex flex-col items-center text-center">
                <motion.div
                  initial={{ y: 10, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center mb-6 ring-1 ring-purple-500/40"
                >
                  <i className="fas fa-wand-magic-sparkles text-purple-300 text-xl"></i>
                </motion.div>

                <h2 className="text-2xl font-bold text-white mb-2">
                  Curriculum Ready
                </h2>
                <p className="text-purple-300 text-sm font-medium tracking-wider uppercase mb-6">
                  AI Personalized Learning
                </p>

                <p className="text-slate-400 text-sm leading-relaxed mb-8 px-4">
                  회원님을 위한 맞춤형 학습 플랜이 생성되었습니다.<br/>
                  지금 바로 첫 번째 레슨을 시작해보세요.
                </p>

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={onStartDemo}
                  className="w-full py-4 rounded-xl bg-white text-purple-900 font-bold text-base hover:bg-purple-50 transition-colors shadow-[0_0_20px_rgba(255,255,255,0.15)] flex items-center justify-center gap-2 group"
                >
                  <span>Start Sample Lesson</span>
                  <i className="fas fa-arrow-right text-sm group-hover:translate-x-1 transition-transform"></i>
                </motion.button>
              </div>
            </div>
          </motion.div>
        ) : (
          isVisible && (
            <motion.div
              key="welcome"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="w-full max-w-5xl z-10 flex flex-col items-center"
            >
              {/* Header Section */}
              <div className="text-center mb-6">
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.2, duration: 0.6 }}
                  className="inline-block mb-4 p-3 rounded-2xl bg-gradient-to-br from-slate-800/50 to-slate-900/50 border border-white/5 backdrop-blur-md shadow-2xl"
                >
                  <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-cyan-500/20 to-purple-500/20 flex items-center justify-center relative overflow-hidden group">
                    <div className="absolute inset-0 bg-gradient-to-tr from-cyan-400/10 to-purple-400/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    <i className="fas fa-code text-3xl bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-purple-400"></i>
                  </div>
                </motion.div>

                <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 tracking-tight">
                  Welcome to <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400">LUNA AI</span>
                </h1>
                <p className="text-slate-400 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
                  무엇을 배우고 싶으신가요? 당신의 목표에 맞춰<br />
                  <span className="text-white font-medium">최적의 학습 로드맵</span>을 설계해드립니다.
                </p>
              </div>

              {/* Scenario Cards Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 w-full mb-8">
                {scenarios.map((card, index) => (
                  <motion.button
                    key={card.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 + index * 0.1 }}
                    onClick={() => onCardClick(card.id, card.prompt)}
                    onMouseEnter={() => setHoveredCard(card.id)}
                    onMouseLeave={() => setHoveredCard(null)}
                    className={`group relative p-6 text-left rounded-2xl bg-slate-900/40 border border-white/5 backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 ${card.bgGradient} ${card.border} ${card.shadow} min-h-[300px]`}
                  >
                    <div className="flex flex-col h-full justify-between">
                      <div>
                        <div className="flex justify-between items-start mb-4">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center bg-slate-800/50 border border-white/5 group-hover:bg-slate-800/80 transition-colors`}>
                            <i className={`fas ${card.icon} text-lg ${card.color}`}></i>
                          </div>
                          <span className={`text-[10px] font-bold px-2 py-1 rounded-full border ${card.badgeColor}`}>
                            {card.badge}
                          </span>
                        </div>

                        <h3 className="text-white font-semibold text-lg mb-2 group-hover:text-cyan-50, transition-colors">
                          {card.title}
                        </h3>
                        <p className="text-slate-400 text-sm leading-relaxed group-hover:text-slate-300">
                          {card.desc}
                        </p>
                      </div>

                      {/* Prompt Preview */}
                      <div className="mt-auto pt-4 border-t border-white/5">
                        <div className="flex items-start gap-2 text-xs text-slate-500 group-hover:text-slate-300 transition-colors">
                          <i className="fas fa-quote-left text-[8px] opacity-50 mt-1"></i>
                          <span className="italic leading-relaxed">{card.prompt}</span>
                        </div>
                      </div>
                    </div>
                  </motion.button>
                ))}
              </div>

              {/* Quick Chips (Optional for flexibility) */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8 }}
                className="flex flex-col items-center gap-3"
              >
                <div className="flex flex-wrap justify-center gap-2">
                  {[
                    { label: '게임 개발', icon: 'fa-gamepad' },
                    { label: '데이터 분석', icon: 'fa-database' },
                    { label: '알고리즘', icon: 'fa-code-branch' },
                    { label: 'AI 챗봇', icon: 'fa-robot' }
                  ].map((chip) => (
                    <button
                      key={chip.label}
                      onClick={() => onTopicSelect(chip.label)}
                      className="px-4 py-2 rounded-full bg-slate-800/30 border border-white/10 hover:bg-slate-700/50 hover:border-cyan-500/30 text-slate-400 hover:text-cyan-400 text-sm transition-all flex items-center gap-2"
                    >
                      <i className={`fas ${chip.icon} text-xs`}></i>
                      {chip.label}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-slate-600">예: "파이썬 배우고 싶어", "자바 기초 알려줘"</p>
              </motion.div>

              {/* Demo Link */}
              <motion.button
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.0 }}
                onClick={onStartDemo}
                className="mt-12 text-slate-500 text-sm hover:text-white transition-colors underline decoration-slate-700 hover:decoration-white underline-offset-4"
              >
                혹은, LUNA 체험판으로 바로 시작하기
              </motion.button>
            </motion.div>
          )
        )}
      </AnimatePresence>
    </div>
  );
};

export default WelcomeScreen;
